import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection } from '../common';

// Medication Form Dialog
const FREQUENCY_OPTIONS = [
  { key: 'once', label: 'Once daily' },
  { key: 'twice', label: 'Twice daily' },
  { key: 'three', label: 'Three times daily' },
  { key: 'asneeded', label: 'As needed' },
  { key: 'weekly', label: 'Weekly' },
];

interface MedicationData {
  name: string;
  dosage?: string;
  frequency?: string;
  prescribed_by?: string;
  pharmacy?: string;
  start_date?: string;
  end_date?: string;
  refill_date?: string;
  notes?: string;
  personId: string;
}

interface MedicationFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: MedicationData) => Promise<void>;
  onDelete?: () => Promise<void>;
  medication?: { id: string; data?: { data?: MedicationData } } | null;
}

export const MedicationFormDialog: React.FC<MedicationFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  medication,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!medication;

  const [name, setName] = useState('');
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('');
  const [prescribedBy, setPrescribedBy] = useState('');
  const [pharmacy, setPharmacy] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [refillDate, setRefillDate] = useState('');
  const [notes, setNotes] = useState('');
  const [personId, setPersonId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (medication) {
        const data = (medication.data?.data || {}) as any;
        setName(data.name || '');
        setDosage(data.dosage || '');
        setFrequency(data.frequency || '');
        setPrescribedBy(data.prescribed_by || data.prescribedBy || '');
        setPharmacy(data.pharmacy || '');
        setStartDate(data.start_date || data.startDate || '');
        setEndDate(data.end_date || data.endDate || '');
        setRefillDate(data.refill_date || data.refillDate || '');
        setNotes(data.notes || '');
        setPersonId(data.personId || '');
      } else {
        setName('');
        setDosage('');
        setFrequency('');
        setPrescribedBy('');
        setPharmacy('');
        setStartDate('');
        setEndDate('');
        setRefillDate('');
        setNotes('');
        setPersonId(memberList[0]?.id || '');
      }
      setError(null);
    }
  }, [open, medication, memberList]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Medication name is required');
      return;
    }
    if (!personId) {
      setError('Please select a family member');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        dosage: dosage.trim() || undefined,
        frequency: frequency || undefined,
        prescribed_by: prescribedBy.trim() || undefined,
        pharmacy: pharmacy.trim() || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        refill_date: refillDate || undefined,
        notes: notes.trim() || undefined,
        personId,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save medication');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete medication');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Medication' : 'New Medication'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Family Member</InputLabel>
              <Select value={personId} label="Family Member" onChange={(e) => setPersonId(e.target.value)}>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>
                        {getMemberName(member.id).charAt(0)}
                      </Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField label="Medication Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Dosage" value={dosage} onChange={(e) => setDosage(e.target.value)} fullWidth placeholder="e.g., 10mg" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select value={frequency} label="Frequency" onChange={(e) => setFrequency(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {FREQUENCY_OPTIONS.map((f) => (
                  <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Prescribed By" value={prescribedBy} onChange={(e) => setPrescribedBy(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Pharmacy" value={pharmacy} onChange={(e) => setPharmacy(e.target.value)} fullWidth />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Refill Date" type="date" value={refillDate} onChange={(e) => setRefillDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Allergy Form Dialog
interface AllergyData {
  name: string;
  severity?: 'mild' | 'moderate' | 'severe';
  reaction?: string;
  treatment?: string;
  epi_pen?: boolean;
  diagnosed_by?: string;
  diagnosed_date?: string;
  notes?: string;
  personId: string;
}

interface AllergyFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AllergyData) => Promise<void>;
  onDelete?: () => Promise<void>;
  allergy?: { id: string; data?: { data?: AllergyData } } | null;
}

export const AllergyFormDialog: React.FC<AllergyFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  allergy,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!allergy;

  const [name, setName] = useState('');
  const [severity, setSeverity] = useState<'mild' | 'moderate' | 'severe'>('mild');
  const [reaction, setReaction] = useState('');
  const [treatment, setTreatment] = useState('');
  const [epiPen, setEpiPen] = useState(false);
  const [diagnosedBy, setDiagnosedBy] = useState('');
  const [diagnosedDate, setDiagnosedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [personId, setPersonId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (allergy) {
        const data = (allergy.data?.data || {}) as any;
        setName(data.name || '');
        setSeverity(data.severity || 'mild');
        setReaction(data.reaction || data.symptoms || '');
        setTreatment(data.treatment || '');
        setEpiPen(data.epi_pen || data.epiPen || false);
        setDiagnosedBy(data.diagnosed_by || data.diagnosedBy || '');
        setDiagnosedDate(data.diagnosed_date || data.diagnosedDate || '');
        setNotes(data.notes || '');
        setPersonId(data.personId || '');
      } else {
        setName('');
        setSeverity('mild');
        setReaction('');
        setTreatment('');
        setEpiPen(false);
        setDiagnosedBy('');
        setDiagnosedDate('');
        setNotes('');
        setPersonId(memberList[0]?.id || '');
      }
      setError(null);
    }
  }, [open, allergy, memberList]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Allergy name is required');
      return;
    }
    if (!personId) {
      setError('Please select a family member');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        severity,
        reaction: reaction.trim() || undefined,
        treatment: treatment.trim() || undefined,
        epi_pen: epiPen || undefined,
        diagnosed_by: diagnosedBy.trim() || undefined,
        diagnosed_date: diagnosedDate || undefined,
        notes: notes.trim() || undefined,
        personId,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save allergy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete allergy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Allergy' : 'New Allergy'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Family Member</InputLabel>
              <Select value={personId} label="Family Member" onChange={(e) => setPersonId(e.target.value)}>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>
                        {getMemberName(member.id).charAt(0)}
                      </Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Allergy" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value as any)}>
                <MenuItem value="mild">Mild</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="severe">Severe</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControlLabel
              control={<Switch checked={epiPen} onChange={(e) => setEpiPen(e.target.checked)} />}
              label="EpiPen"
              sx={{ mt: 1 }}
            />
          </Grid>

          <Grid size={12}>
            <TextField label="Reaction" value={reaction} onChange={(e) => setReaction(e.target.value)} fullWidth placeholder="e.g., Hives, swelling, difficulty breathing" />
          </Grid>
          <Grid size={12}>
            <TextField label="Treatment" value={treatment} onChange={(e) => setTreatment(e.target.value)} fullWidth placeholder="e.g., Epinephrine, antihistamines" />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Diagnosed By" value={diagnosedBy} onChange={(e) => setDiagnosedBy(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Diagnosed Date" type="date" value={diagnosedDate} onChange={(e) => setDiagnosedDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Illness Form Dialog
interface IllnessData {
  name: string;
  startDate?: string;
  endDate?: string;
  symptoms?: string;
  treatment?: string;
  doctor?: string;
  recovered?: boolean;
  notes?: string;
  personId: string;
}

interface IllnessFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: IllnessData) => Promise<void>;
  onDelete?: () => Promise<void>;
  illness?: { id: string; data?: { data?: IllnessData } } | null;
}

export const IllnessFormDialog: React.FC<IllnessFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  illness,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!illness;

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [treatment, setTreatment] = useState('');
  const [doctor, setDoctor] = useState('');
  const [recovered, setRecovered] = useState(false);
  const [notes, setNotes] = useState('');
  const [personId, setPersonId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (illness) {
        const data = (illness.data?.data || {}) as any;
        setName(data.name || '');
        setStartDate(data.startDate || data.start_date || '');
        setEndDate(data.endDate || data.end_date || '');
        setSymptoms(data.symptoms || '');
        setTreatment(data.treatment || '');
        setDoctor(data.doctor || '');
        setRecovered(data.recovered || false);
        setNotes(data.notes || '');
        setPersonId(data.personId || '');
      } else {
        setName('');
        setStartDate('');
        setEndDate('');
        setSymptoms('');
        setTreatment('');
        setDoctor('');
        setRecovered(false);
        setNotes('');
        setPersonId(memberList[0]?.id || '');
      }
      setError(null);
    }
  }, [open, illness, memberList]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Illness name is required');
      return;
    }
    if (!personId) {
      setError('Please select a family member');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        symptoms: symptoms.trim() || undefined,
        treatment: treatment.trim() || undefined,
        doctor: doctor.trim() || undefined,
        recovered: recovered || undefined,
        notes: notes.trim() || undefined,
        personId,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save illness');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete illness');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Illness' : 'New Illness'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>Family Member</InputLabel>
              <Select value={personId} label="Family Member" onChange={(e) => setPersonId(e.target.value)}>
                {memberList.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: getMemberColor(member.id), fontSize: 12 }}>
                        {getMemberName(member.id).charAt(0)}
                      </Avatar>
                      {getMemberName(member.id)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField label="Illness Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel
              control={<Switch checked={recovered} onChange={(e) => setRecovered(e.target.checked)} />}
              label="Recovered"
              sx={{ mt: 1 }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Doctor" value={doctor} onChange={(e) => setDoctor(e.target.value)} fullWidth />
          </Grid>

          <Grid size={12}>
            <TextField label="Symptoms" value={symptoms} onChange={(e) => setSymptoms(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
          <Grid size={12}>
            <TextField label="Treatment" value={treatment} onChange={(e) => setTreatment(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Healthcare Provider Form Dialog
const HEALTHCARE_CATEGORIES = [
  { key: 'doctor', label: 'Doctor' },
  { key: 'dentist', label: 'Dentist' },
  { key: 'pediatrician', label: 'Pediatrician' },
  { key: 'specialist', label: 'Specialist' },
  { key: 'therapist', label: 'Therapist' },
  { key: 'optometrist', label: 'Optometrist' },
  { key: 'dermatologist', label: 'Dermatologist' },
  { key: 'orthodontist', label: 'Orthodontist' },
];

export interface HealthcareProviderData {
  first: string;
  last: string;
  category: string;
  specialty?: string;
  organization?: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
}

interface HealthcareProviderFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: HealthcareProviderData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  provider?: { id: string; family_id?: string; data?: { data?: any } } | null;
}

const formatProviderPhone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

export const HealthcareProviderFormDialog: React.FC<HealthcareProviderFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  provider,
}) => {
  const isEditing = !!provider;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection(provider?.family_id ? [provider.family_id] : undefined);

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [category, setCategory] = useState('doctor');
  const [specialty, setSpecialty] = useState('');
  const [organization, setOrganization] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (provider) {
        const data = (provider.data?.data || {}) as any;
        setFirst(data.first || '');
        setLast(data.last || '');
        setCategory(data.category || data.role || 'doctor');
        setSpecialty(data.specialty || '');
        setOrganization(data.organization || data.practice || '');
        setPhone(data.phone ? formatProviderPhone(data.phone) : '');
        setEmail(data.email || '');
        setAddress(data.address || '');
        setNotes(data.notes || '');
      } else {
        setFirst('');
        setLast('');
        setCategory('doctor');
        setSpecialty('');
        setOrganization('');
        setPhone('');
        setEmail('');
        setAddress('');
        setNotes('');
      }
      setError(null);
    }
  }, [open, provider]);

  const handleSave = async () => {
    if (!first.trim()) {
      setError('First name is required');
      return;
    }
    if (selectedFamilyIds.length === 0) {
      setError('Please select at least one family');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(
        {
          first: first.trim(),
          last: last.trim(),
          category,
          specialty: specialty.trim() || undefined,
          organization: organization.trim() || undefined,
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
          address: address.trim() || undefined,
          notes: notes.trim() || undefined,
        },
        selectedFamilyIds
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save provider');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete provider');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Provider' : 'New Healthcare Provider'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="First Name" value={first} onChange={(e) => setFirst(e.target.value)} fullWidth required autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Last Name" value={last} onChange={(e) => setLast(e.target.value)} fullWidth />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
                {HEALTHCARE_CATEGORIES.map((c) => (
                  <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Specialty" value={specialty} onChange={(e) => setSpecialty(e.target.value)} fullWidth placeholder="e.g., Cardiology, Orthodontics" />
          </Grid>

          <Grid size={12}>
            <TextField label="Organization / Practice" value={organization} onChange={(e) => setOrganization(e.target.value)} fullWidth placeholder="e.g., Children's Hospital, Smile Dental" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Phone" value={phone} onChange={(e) => setPhone(formatProviderPhone(e.target.value))} fullWidth type="tel" placeholder="(123) 456-7890" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth type="email" />
          </Grid>

          <Grid size={12}>
            <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default {
  MedicationFormDialog,
  AllergyFormDialog,
  IllnessFormDialog,
  HealthcareProviderFormDialog,
};
